import { useMemo, useState } from 'react'
import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import {
  characterStyleToCss,
  effectiveCharacterStyle,
  pickLegibleBackdrop,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCharacterStyleLayer,
  EditorFieldRunAddress,
  EditorResolvedStyle,
  EditorTextBoxMount,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import EditorTextBoxStyleBar from './EditorTextBoxStyleBar'
import EditorTextBoxModalShell from './EditorTextBoxModalShell'
import type {
  EditorTextBoxModalAttrs,
  EditorTextBoxModalTestId,
} from './EditorTextBoxModalShell'
import * as S from './EditorTextBoxModal.styled'

export type AuthoringStyleDraft = {
  draft: EditorCharacterStyleLayer
  effective: EditorResolvedStyle
  authored: EditorCharacterStyleLayer
  commitStyle: (patch: EditorCharacterStyleLayer) => void
  resetDraft: () => void
  styleDirty: boolean
  styleForWrite: () => EditorCharacterStyleLayer | undefined
}

export const useAuthoringStyleDraft = (
  seed: EditorCharacterStyleLayer | EditorResolvedStyle | null | undefined,
): AuthoringStyleDraft => {
  const [draft, setDraft] = useState<EditorCharacterStyleLayer>({})
  const effective = useMemo(
    () => effectiveCharacterStyle(seed, draft),
    [seed, draft],
  )
  const authored = useMemo(() => ({ ...(seed ?? {}), ...draft }), [seed, draft])
  const commitStyle = (patch: EditorCharacterStyleLayer) =>
    setDraft((d) => ({ ...d, ...patch }))
  const resetDraft = () => setDraft({})
  const styleDirty = Object.keys(draft).length > 0
  const styleForWrite = () => (styleDirty ? { ...draft } : undefined)
  return {
    draft,
    effective,
    authored,
    commitStyle,
    resetDraft,
    styleDirty,
    styleForWrite,
  }
}

/** A field run in the box: the block it sits in and its run ordinal there. */
export type FieldAddress = { blockIndex: number; runIndex: number }

/**
 * The field run a modal edits, or null when it inserts instead: the run it
 * was opened on (a double-click on the field), else the box's first run of
 * that key (the toolbar's configure button on a field the box already holds).
 */
export const seedFieldRun = (
  mount: EditorTextBoxMount | null,
  fieldKey: string,
  field: FieldAddress | null | undefined,
): EditorFieldRunAddress | null => {
  const run = field
    ? mount?.fieldRunAt?.(field.blockIndex, field.runIndex)
    : mount?.fieldRun?.(fieldKey)
  return run && run.fieldKey === fieldKey ? run : null
}

export type AuthoringCommitFn = () => unknown

export type AuthoringCommitState = {
  saving: boolean
  error: string | null
  setError: Dispatch<SetStateAction<string | null>>
  run: (commit: AuthoringCommitFn, failMessage: string) => Promise<boolean>
}

export const useAuthoringCommit = (): AuthoringCommitState => {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = async (commit: AuthoringCommitFn, failMessage: string) => {
    setSaving(true)
    setError(null)
    const ok = await commit()
    setSaving(false)
    if (!ok) {
      setError(failMessage)
      return false
    }
    return true
  }
  return { saving, error, setError, run }
}

export const AUTHORING_PREVIEW_DEFAULT_STYLE: EditorResolvedStyle =
  effectiveCharacterStyle(null, {})

export type AuthoringPreviewCardProps = {
  effective: EditorResolvedStyle
  text?: string
  ghost?: ReactNode
  cardAttrs?: EditorTextBoxModalAttrs
  textAttrs?: EditorTextBoxModalAttrs
  ghostAttrs?: EditorTextBoxModalAttrs
  cardProps?: Pick<ComponentProps<typeof S.PreviewCard>, '$block'>
  textProps?: Pick<ComponentProps<typeof S.PreviewText>, '$block'>
}

export const AuthoringPreviewCard = ({
  effective,
  text,
  ghost,
  cardAttrs,
  textAttrs,
  ghostAttrs,
  cardProps,
  textProps,
}: AuthoringPreviewCardProps) => {
  const previewStyle = characterStyleToCss(effective)
  const backdrop = text ? pickLegibleBackdrop(effective.color) : undefined
  return (
    <S.PreviewCard
      {...cardAttrs}
      {...cardProps}
      style={backdrop ? { background: backdrop } : undefined}
    >
      {text ? (
        <S.PreviewText {...textAttrs} {...textProps} style={previewStyle}>
          {text}
        </S.PreviewText>
      ) : (
        <S.PreviewGhost {...ghostAttrs}>{ghost}</S.PreviewGhost>
      )}
    </S.PreviewCard>
  )
}

export type AuthoringStyleSectionProps = {
  effective: EditorResolvedStyle
  onCommit: (patch: EditorCharacterStyleLayer) => void
  fontFamilies: readonly string[]
  idp: string
  barAttrs?: EditorTextBoxModalAttrs
  label?: string
  children?: ReactNode
}

export const AuthoringStyleSection = ({
  effective,
  onCommit,
  fontFamilies,
  idp,
  barAttrs,
  label,
  children,
}: AuthoringStyleSectionProps) => {
  const { t } = useTranslation()
  return (
    <S.Section>
      <S.SectionLabel>
        {label ?? t('editor.textBox.modals.common.styling')}
      </S.SectionLabel>
      <S.StyleBar {...barAttrs}>
        <EditorTextBoxStyleBar
          effective={effective}
          onCommit={onCommit}
          fontFamilies={fontFamilies}
          idp={idp}
        />
      </S.StyleBar>
      {children}
    </S.Section>
  )
}

export type AuthoringModalFrameProps = {
  backdropTestId: EditorTextBoxModalTestId
  onDismiss: () => void
  onEscape?: () => void
  dialogAttrs?: EditorTextBoxModalAttrs
  ariaLabel: string
  title: string
  subtitle?: ReactNode
  closeAttrs?: EditorTextBoxModalAttrs
  children: ReactNode
  footer?: ReactNode
}

export const AuthoringModalFrame = ({
  backdropTestId,
  onDismiss,
  onEscape,
  dialogAttrs,
  ariaLabel,
  title,
  subtitle,
  closeAttrs,
  children,
  footer,
}: AuthoringModalFrameProps) => {
  const { t } = useTranslation()
  return (
    <EditorTextBoxModalShell
      backdropTestId={backdropTestId}
      onDismiss={onDismiss}
      onEscape={onEscape}
    >
      <S.Dialog {...dialogAttrs} role="dialog" aria-label={ariaLabel}>
        <S.Header>
          <S.Title>{title}</S.Title>
          <S.Subtitle>{subtitle}</S.Subtitle>
          <S.CloseBtn
            type="button"
            {...closeAttrs}
            title={t('editor.textBox.modals.common.close')}
            onClick={onDismiss}
          >
            <Icon icon="exitModal" width="16px" height="16px" />
          </S.CloseBtn>
        </S.Header>
        {children}
        {footer}
      </S.Dialog>
    </EditorTextBoxModalShell>
  )
}

/**
 * The footer's second commit, beside the primary: an insert modal's "save
 * the case, insert nothing". Disabled while there is nothing to save.
 */
export type AuthoringSecondaryAction = {
  attrs?: EditorTextBoxModalAttrs
  label: string
  title?: string
  disabled?: boolean
  onClick: () => void
}

export type AuthoringFooterProps = {
  danger?: ReactNode
  cancelAttrs?: EditorTextBoxModalAttrs
  onCancel: () => void
  secondary?: AuthoringSecondaryAction | null
  primaryAttrs?: EditorTextBoxModalAttrs
  primaryLabel: string
  onPrimary: () => void
  saving?: boolean
}

export const AuthoringFooter = ({
  danger = null,
  cancelAttrs,
  onCancel,
  secondary = null,
  primaryAttrs,
  primaryLabel,
  onPrimary,
  saving = false,
}: AuthoringFooterProps) => {
  const { t } = useTranslation()
  return (
    <S.Footer>
      {danger}
      <S.FooterSpacer />
      <S.GhostBtn type="button" {...cancelAttrs} onClick={onCancel}>
        {t('editor.textBox.modals.common.cancel')}
      </S.GhostBtn>
      {secondary && (
        <S.SecondaryBtn
          type="button"
          {...secondary.attrs}
          title={secondary.title}
          disabled={saving || secondary.disabled}
          onClick={secondary.onClick}
        >
          {secondary.label}
        </S.SecondaryBtn>
      )}
      <S.PrimaryBtn
        type="button"
        {...primaryAttrs}
        disabled={saving}
        onClick={onPrimary}
      >
        {saving ? t('editor.textBox.modals.common.saving') : primaryLabel}
      </S.PrimaryBtn>
    </S.Footer>
  )
}
